import { Button, Drawer, Stack, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { Control, FieldErrors, useFieldArray } from 'react-hook-form'
import { Fade, IconButton, Icons } from 'utoniq-ui'

import { Item, PreSaleStatus } from '@/clients/utoniq-core/schema'
import { ThroughTicketTag } from '@/components/organisms'
import { useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { ConversionCategories } from '@/core/analytics/ConversionKeys'
import { Cell, CellFrame } from '@/core/architecture'
import { App } from '@/core/native'

import { ApplyPreSaleFirstComeFirstServedValue } from '../FirstComeFirstServedDetailForm'
import {
  QuantitySelectModal,
  QuantitySelectModalProps,
} from './QuantitySelectModal'
import { SkuUnitSelectModal } from './SkuUnitSelectModal'
import { TicketSelectModal } from './TicketSelectModal'

type TicketListCellProps = {
  preSaleId: string
  items: Partial<Item>[]
  isBeforeReceiption: boolean
  maxChoice?: number
  maxSelectItem?: number
  preSaleStatus: PreSaleStatus
  control: Control<ApplyPreSaleFirstComeFirstServedValue>
  errors: FieldErrors<ApplyPreSaleFirstComeFirstServedValue>
}

const MAX_ADD_ITEM = 3
const MAX_SELECT_ITEM = 8

export const TicketListCell: Cell<TicketListCellProps> = ({
  preSaleId,
  items,
  preSaleStatus,
  maxChoice = MAX_ADD_ITEM,
  maxSelectItem = MAX_SELECT_ITEM,
  control,
  isBeforeReceiption,
}) => {
  const { t } = useTranslation('firstComeFirstServedDetail')
  const [open, setOpen] = useState<boolean>(false)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const { fields: choiceFields, update: updateChoice } = useFieldArray({
    control,
    name: 'choices',
  })
  const {
    fields: purchaseCountChoiceFields,
    update: updatePurchaseCountChoice,
  } = useFieldArray({
    control,
    name: 'purchaseCountChoices',
  })

  return (
    <CellFrame sx={{ pt: 3, pb: 7 }}>
      <Typography
        variant="h6"
        sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}
        color="text.secondary"
      >
        {t('selectTicketDetails', '申込内容の選択')}
      </Typography>
      {maxChoice > 1 ? (
        <Stack sx={{ py: 4 }}>
          <Typography component="p" variant="caption">
            {t('selectTicketDesc', { max: maxChoice })}
          </Typography>
        </Stack>
      ) : (
        <Stack height={8} />
      )}
      <Stack spacing={4}>
        {choiceFields.map(({ id, itemId, skuId }, index) => {
          const item = items?.find(({ id }) => id === itemId)
          const skuUnit = skuId
            ? item?.skuSettings?.units.find(sku => sku.id === skuId)
            : null
          return (
            <Stack key={index} sx={{ position: 'relative' }}>
              <Button
                key={id}
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 2,
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  pointerEvents: item ? 'none' : 'unset',
                }}
                onClick={() => {
                  setSelectedIndex(index)
                  setOpen(true)
                }}
              >
                {maxChoice > 1 && (
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    lineHeight="30px"
                  >
                    {t('preference', { number: index + 1 })}
                  </Typography>
                )}
                {item ? (
                  <Stack sx={{ mr: 7 }}>
                    <Typography variant="subtitle1" align="left">
                      {item.title}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {!!skuUnit?.unitName && (
                        <Typography variant="body2" align="left">
                          {skuUnit.unitName}
                        </Typography>
                      )}
                      {item.metadata?.ticket?.isThroughTicket && (
                        <ThroughTicketTag />
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ pt: 1 }}
                    >
                      <Typography variant="subtitle1">
                        ¥{skuUnit?.price || item.formattedPrice}/
                      </Typography>
                      <Typography variant="subtitle1">
                        {purchaseCountChoiceFields[index]?.count}
                        {t('sheet', '枚')}
                      </Typography>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" sx={{ py: 1 }}>
                    <Typography variant="button" color="primary">
                      {t('selectTicket', 'チケットを選択する')}
                    </Typography>
                    <Icons.Next color="primary" />
                  </Stack>
                )}
              </Button>
              {item && (
                <IconButton.Close
                  size="small"
                  bgcolor="grey.50"
                  sx={{ position: 'absolute', zIndex: 2, right: 16, top: 16 }}
                  onClick={() => {
                    updateChoice(index, { itemId: '' })
                    updatePurchaseCountChoice(index, { count: 1 })
                  }}
                />
              )}
            </Stack>
          )
        })}
      </Stack>
      <TicketBottomSheet
        preSaleId={preSaleId}
        isBeforeReceiption={isBeforeReceiption}
        items={items}
        selectedIndex={selectedIndex}
        selectedItems={choiceFields}
        preSaleStatus={preSaleStatus}
        open={open}
        maxChoice={maxChoice}
        maxSelectItem={maxSelectItem}
        onClose={() => setOpen(false)}
        onSubmit={async ({ choiceId, choiceSkuId, purchaseCountChoice }) => {
          updateChoice(selectedIndex, { itemId: choiceId, skuId: choiceSkuId })
          updatePurchaseCountChoice(selectedIndex, {
            count: purchaseCountChoice,
          })
        }}
      />
    </CellFrame>
  )
}

type TicketBottomSheetProps = {
  preSaleId: string
  isBeforeReceiption: boolean
  items: Partial<Item>[]
  selectedItems: ApplyPreSaleFirstComeFirstServedValue['choices']
  open: boolean
  maxChoice: number
  selectedIndex: number
  maxSelectItem: number
  preSaleStatus: PreSaleStatus
  onClose: () => void
  onSubmit: QuantitySelectModalProps['onSubmit']
}
const TicketBottomSheet: FC<TicketBottomSheetProps> = ({
  preSaleId,
  isBeforeReceiption,
  items,
  selectedItems,
  open,
  maxChoice,
  preSaleStatus,
  selectedIndex,
  maxSelectItem,
  onClose,
  onSubmit,
}) => {
  const { analytics } = useAnalytics()
  const [step, setStep] = useState<number>(0)
  const [item, setItem] = useState<{
    item: Partial<Item> | undefined // アイテムデータ
    selectSkuId: string | undefined // SKUがある場合、選択したSKU ID
  } | null>(null)

  const handleOnClose = () => {
    onClose()
    setStep(0)
    setItem(null)
  }

  return (
    <Drawer open={open} onClose={handleOnClose}>
      <Stack height="75dvh" sx={{ bgcolor: 'background.default' }}>
        {step === 0 && (
          <Fade>
            <TicketSelectModal
              items={items}
              preSaleStatus={preSaleStatus}
              selectedItems={selectedItems}
              onClose={onClose}
              onSelectItem={item => {
                analytics?.track('select_item', {
                  item_list_id: `preSale_list_${preSaleId}`,
                  item_list_name: 'PreSale Items',
                  items: [
                    {
                      item_id: item?.id,
                      item_name: item?.title,
                      item_category: ConversionCategories.preSaleItem,
                      item_category2: item?.type,
                      item_category3: App.getPlatform(),
                      item_category4: 'FirstComeFirstServedDetail',
                      item_category5: preSaleId,
                      price: item?.price,
                    },
                  ],
                })
                setItem({ item, selectSkuId: undefined })
                if (item?.skuSettings?.units?.length) {
                  return setStep(1)
                }
                setStep(2)
              }}
              isBeforeReceiption={isBeforeReceiption}
            />
          </Fade>
        )}
        {step === 1 && item?.item && (
          <Fade>
            <SkuUnitSelectModal
              item={item?.item}
              selectedItems={selectedItems}
              onClose={handleOnClose}
              onGoBack={() => {
                setStep(0)
                setItem(null)
              }}
              isBeforeReceiption={isBeforeReceiption}
              onSelectItem={skuId => {
                setItem({ item: item?.item, selectSkuId: skuId })
                setStep(2)
              }}
            />
          </Fade>
        )}
        {step === 2 && item?.item && (
          <Fade>
            <QuantitySelectModal
              item={item.item}
              skuId={item.selectSkuId}
              maxChoice={maxChoice}
              selectedIndex={selectedIndex}
              onGoBack={() => {
                if (item.item?.skuSettings?.units?.length) {
                  return setStep(1)
                }
                setStep(0)
              }}
              maxSelectItem={maxSelectItem}
              onSubmit={async values => {
                await onSubmit(values)
                onClose()
                setStep(0)
                setItem(null)
              }}
            />
          </Fade>
        )}
      </Stack>
    </Drawer>
  )
}
